/*
 * every upload error code has a named handler here: the Ops sentence the panel shows and what the
 * panel does next.
 */
import { api_error } from '../../format';
import { APPLY_UNSURE_MESSAGE, UPLOAD_CATEGORY_REFUSED, UPLOAD_ERROR as E } from './params';

// What the panel does after showing the message.
export const NEXT = {
  NONE: 'none',
  // The preview no longer matches the data: Apply is withdrawn and Ops upload the file again.
  REUPLOAD: 'reupload',
  // The run changed state elsewhere (applied, discarded): re-read it so the preview shows why.
  RELOAD_RUN: 'reload_run',
  // The run is gone: close the preview and re-read the history.
  CLOSE_RUN: 'close_run',
  // The shell's no-permission state owns a 401/403.
  PERMISSION: 'permission'
};

const ASK_ENGINEERING = 'Nothing was saved. Ask engineering.';

/*
 * text: shown when the server sent no message, or always when `ours` (the server's text for these
 * codes is developer wording).
 */
export const HANDLERS = {
  [E.INVALID_REQUEST]: {
    ours: true,
    text: 'The request was not accepted. Reload the page and try again.',
    next: NEXT.NONE
  },
  [E.INVALID_PAY_MONTH]: {
    ours: true,
    text: 'The payment month was not sent correctly. Reload the page and try again.',
    next: NEXT.NONE
  },
  [E.PAY_MONTH_OUTSIDE_WINDOW]: {
    text: 'Only the current and the previous payment month can take an upload. Nothing was saved.',
    next: NEXT.NONE
  },
  [E.FILE_TOO_LARGE]: {
    text: 'The file is larger than 2 MB. Split it into smaller files and upload each one.',
    next: NEXT.NONE
  },
  [E.FILE_NOT_CSV]: {
    text: 'The file is not a CSV. Save it as CSV UTF-8 and upload again.',
    next: NEXT.NONE
  },
  [E.FILE_EMPTY]: {
    text: 'The file has no data rows. Nothing was saved.',
    next: NEXT.NONE
  },
  [E.MISSING_COLUMN]: {
    text: 'A required column is missing. Download the template and use its header row.',
    next: NEXT.NONE
  },
  [E.UNKNOWN_COLUMN]: {
    text: 'The file has a column the template does not. Download the template and use its header row.',
    next: NEXT.NONE
  },
  [E.DUPLICATE_COLUMN]: {
    text: 'A column appears twice in the header row. Download the template and use its header row.',
    next: NEXT.NONE
  },
  [E.ROW_LIMIT_EXCEEDED]: {
    text: 'The file has more than 1,000 rows. Split it into smaller files and upload each one.',
    next: NEXT.NONE
  },
  [E.FILE_ALREADY_APPLIED]: {
    text: 'This exact file was already applied for this month. Nothing was saved.',
    next: NEXT.NONE
  },
  [E.REASON_REQUIRED]: {
    text: 'A reason of 1 to 1,024 characters is required.',
    next: NEXT.NONE
  },
  [E.RUN_NOT_FOUND]: {
    text: 'This upload no longer exists. Upload the file again.',
    next: NEXT.CLOSE_RUN
  },
  [E.TEACHER_NOT_FOUND]: {
    text: 'That teacher could not be found.',
    next: NEXT.NONE
  },
  // only the uploader or a SUPER_USER may act; the re-read hides Apply/Discard for this viewer.
  [E.RUN_NOT_YOURS]: {
    text: 'Only the person who uploaded this file can apply or discard it. Upload the file yourself to apply it.',
    next: NEXT.RELOAD_RUN
  },
  [E.RUN_DISCARDED]: {
    text: 'This upload was discarded. Upload the file again to apply it.',
    next: NEXT.RELOAD_RUN
  },
  [E.ALREADY_APPLIED]: {
    text: 'This upload is already applied, so it cannot be discarded. Remove entries in the list below instead.',
    next: NEXT.RELOAD_RUN
  },
  [E.INVALID_ROWS_NOT_ACKNOWLEDGED]: {
    text: 'This upload has invalid rows. Tick "Skip the invalid rows" to apply the others.',
    next: NEXT.NONE
  },
  // the server names the rows still undecided ("Keep or skip rows 3, 4 before applying.")
  [E.CONFIRMATION_REQUIRED]: {
    text: 'Some flagged rows have no decision yet. Keep or skip each one, then apply.',
    next: NEXT.NONE
  },
  [E.NOTHING_TO_APPLY]: {
    ours: true,
    text: 'Every row was skipped, so nothing would be saved. Discard this upload instead.',
    next: NEXT.NONE
  },
  [E.PREVIEW_STALE]: {
    text: 'Some rows changed since the preview. Nothing was saved. Upload the file again.',
    next: NEXT.REUPLOAD
  },
  [E.IDEMPOTENCY_MISMATCH]: {
    text: 'This upload was already applied with different decisions. Nothing was applied again.',
    next: NEXT.RELOAD_RUN
  },
  [E.MONTH_CLOSED]: {
    text: "A teacher's month in this file is closed. Nothing was saved. Reopen it, then upload the file again.",
    next: NEXT.REUPLOAD
  },
  [E.MONTH_NOT_OPEN]: {
    text: "A teacher's month in this file is not open. Nothing was saved. Reopen it, then upload the file again.",
    next: NEXT.REUPLOAD
  },
  [E.PREVIOUS_MONTH_NO_RECORD]: {
    text: 'A teacher in this file has no payment record for this month. Nothing was saved. Upload the file again.',
    next: NEXT.REUPLOAD
  },
  // storage failures name the cause, so Ops know whether to retry or to ask engineering.
  [E.UPLOAD_STORAGE_NOT_CONFIGURED]: {
    text: `File storage is not set up on this server, so the file could not be kept. ${ASK_ENGINEERING}`,
    next: NEXT.NONE
  },
  [E.UPLOAD_BUCKET_MISSING]: {
    text: `The storage bucket for uploaded files was not found, so the file could not be kept. ${ASK_ENGINEERING}`,
    next: NEXT.NONE
  },
  [E.UPLOAD_ACCESS_DENIED]: {
    text: `This server is not allowed to write to the storage bucket. ${ASK_ENGINEERING}`,
    next: NEXT.NONE
  },
  [E.UPLOAD_TIMED_OUT]: {
    text: 'Uploading the file to storage timed out. Nothing was saved. Try again.',
    next: NEXT.NONE
  },
  [E.UPLOAD_FAILED]: {
    text: 'Uploading the file to storage failed. Nothing was saved. Try again, then ask engineering.',
    next: NEXT.NONE
  },
  [E.FILE_LINK_UNAVAILABLE]: {
    text: 'The link to the original file could not be created. Try again, then ask engineering.',
    next: NEXT.NONE
  },
  [E.FORBIDDEN]: {
    text: 'You do not have the teacher payments permission.',
    next: NEXT.PERMISSION
  },
  [E.LOOKUP_FAILED]: {
    text: 'The server could not read the data it needed. Nothing was saved. Try again.',
    next: NEXT.NONE
  }
};

export const REQUEST_FAILED_TEXT = 'The request failed. Try again.';

export const HISTORY_FAILED_TEXT = 'Could not load the uploads. Try again.';

// for apply: no answer or a 5xx may still have committed; the run uuid makes a second press a
// replay.
export const APPLY_UNCERTAIN_TEXT =
  'The apply may have gone through. Press Apply again. It will not pay twice.';

// A validate with no answer may still have created a run; a new upload replaces it.
export const UPLOAD_UNCERTAIN_TEXT =
  'The upload got no answer. It may still have been checked: look under Earlier uploads, or choose the file again.';

const list = (v) => (Array.isArray(v) ? v : []);

/*
 * an apply may have committed only when no answer came, a 5xx came without the server's code or
 * sentence (a proxy), or the server said it cannot tell.
 */
const apply_uncertain = (status, code, server_message) =>
  !status ||
  (status >= 500 && (!code || !server_message || server_message === APPLY_UNSURE_MESSAGE));

/*
 * surface: 'apply' marks a failure that may have committed (no answer / 5xx) as uncertain;
 * 'upload' does the same for a validate that got no answer at all. Known codes keep their handler.
 */
export const upload_error_view = (e, surface = '') => {
  const res = (e && e.response) || {};
  const body = res.data || {};
  const { status } = res;
  const { code, message } = api_error(e);
  const handler = HANDLERS[code] || null;
  // RUN_NOT_YOURS is a 403 about this run, not a missing grant: it stays in the panel.
  const is_permission = (status === 401 || status === 403) && code !== E.RUN_NOT_YOURS;
  const uncertain =
    (surface === 'apply' && !is_permission && apply_uncertain(status, code, message)) ||
    (surface === 'upload' && !status);
  const field = (list(body.fields)[0] || {}).field || '';
  const category_refused = code === UPLOAD_CATEGORY_REFUSED && field === 'category';
  let text = REQUEST_FAILED_TEXT;
  if (handler) text = handler.ours ? handler.text : message || handler.text;
  else if (message) text = message;
  // a refused decisions list: the server names the line ("row 5 is invalid ...")
  if (code === E.INVALID_REQUEST && field === 'decisions')
    text = `A decision was not accepted${message ? ` (${message})` : ''}. Reload the upload and decide again.`;
  // the server's sentence names the refused value ("category Bonus is not one of ...")
  if (category_refused && !message)
    text = 'The category chosen for the file is not a payment category. Pick one from the list.';
  if (uncertain) text = surface === 'apply' ? APPLY_UNCERTAIN_TEXT : UPLOAD_UNCERTAIN_TEXT;
  return {
    code,
    status,
    is_permission,
    uncertain,
    next: is_permission ? NEXT.PERMISSION : (handler && handler.next) || NEXT.NONE,
    message: text,
    // The server's own sentence, when it sent one ('' otherwise).
    server_message: message || '',
    stale_rows: list(body.stale_rows).map((r) => ({
      line: r.line,
      message: r.message || `Row ${r.line} changed since the preview.`
    })),
    applied_run: body.applied_run && body.applied_run.run_uuid ? body.applied_run : null,
    // the file's category (the dropdown) was refused, not a row: the dropdown is marked invalid.
    category_refused
  };
};
